import { Button, Col, Row } from 'react-bootstrap';

import { BsDownload } from 'react-icons/bs';
import { Card } from '../../../app/components/Card';
import { useProvider } from '../../../app/hooks/providers';
import { useDocumentTitle } from '../../../app/hooks/use-document-title';

interface FileObject {
  id?: string;
  file: string;
}

const FileCardInfo = ({
  label,
  file,
  index,
  isLoading = false,
}: {
  label: string;
  file?: FileObject;
  isLoading?: boolean;
  index: number;
}) => {
  return (
    <Card.Info label={`${label} #${index + 1}`} isLoading={isLoading}>
      {!!file?.file && (
        <Button
          size="sm"
          variant="outline-primary"
          href={file.file}
          target="_blank"
          rel="noreferrer"
        >
          <BsDownload className="tw:inline" /> Download {label} #{index + 1}
        </Button>
      )}
    </Card.Info>
  );
};

// eslint-disable-next-line sonarjs/cognitive-complexity
export const Education = () => {
  useDocumentTitle('Provider Education');
  const { isProviderLoading, provider } = useProvider();

  return (
    <Row className="align-items-stretch">
      <Col xs={12} md={6} lg={4} className="mb-4">
        <Card.SubCard className="p-4 h-100">
          <h5>Languages</h5>
          {isProviderLoading && (
            <>
              <FileCardInfo label="Medical Diploma" index={0} isLoading />
              <FileCardInfo label="Medical Diploma" index={1} isLoading />
              <FileCardInfo label="Medical Diploma" index={2} isLoading />
            </>
          )}
          <Card.Info label="Languages Spoken" skeletonLines={3} isLoading={isProviderLoading}>
            {provider?.languages_spoken?.map((language) => (
              <div key={language}>{window.LANGUAGES[language]}</div>
            ))}
          </Card.Info>
        </Card.SubCard>
      </Col>

      <Col xs={12} md={6} lg={4} className="mb-4">
        <Card.SubCard className="p-4 mb-4 h-100">
          <h5>Medical Diplomas</h5>
          {isProviderLoading && (
            <>
              <FileCardInfo label="Medical Diploma" index={0} isLoading />
              <FileCardInfo label="Medical Diploma" index={1} isLoading />
              <FileCardInfo label="Medical Diploma" index={2} isLoading />
            </>
          )}
          {provider?.medical_diplomas?.map((diploma, i) => (
            <FileCardInfo key={diploma.id} label="Medical Diploma" file={diploma} index={i} />
          ))}
          {provider?.medical_diplomas?.length === 0 && <>No Medical Diplomas registered</>}
        </Card.SubCard>
      </Col>
      <Col xs={12} md={6} lg={4} className="mb-4">
        <Card.SubCard className="p-4 mb-4 h-100">
          <h5>Internships</h5>
          {isProviderLoading && (
            <>
              <FileCardInfo label="Internships" index={0} isLoading />
              <FileCardInfo label="Internships" index={1} isLoading />
              <FileCardInfo label="Internships" index={2} isLoading />
            </>
          )}
          {provider?.internships?.map((internship, i) => (
            <FileCardInfo key={internship.id} label="Internships" file={internship} index={i} />
          ))}
          {provider?.internships?.length === 0 && <>No Internships registered</>}
        </Card.SubCard>
      </Col>
      <Col xs={12} md={6} lg={4} className="mb-4">
        <Card.SubCard className="p-4 mb-4 h-100">
          <h5>Residency Certificates</h5>
          {isProviderLoading && (
            <>
              <FileCardInfo label="Residency Certificate" index={0} isLoading />
              <FileCardInfo label="Residency Certificate" index={1} isLoading />
              <FileCardInfo label="Residency Certificate" index={2} isLoading />
            </>
          )}
          {provider?.residency_certificates?.map((residency_certificate, i) => (
            <FileCardInfo
              key={residency_certificate.id}
              label="Residency Certificate"
              file={residency_certificate}
              index={i}
            />
          ))}
          {provider?.residency_certificates?.length === 0 && (
            <>No Residency Certificates registered</>
          )}
        </Card.SubCard>
      </Col>
      <Col xs={12} md={6} lg={4} className="mb-4">
        <Card.SubCard className="p-4 mb-4 h-100">
          <h5>Fellowship Certificates</h5>
          {isProviderLoading && (
            <>
              <FileCardInfo label="Fellowship Certificates" index={0} isLoading />
              <FileCardInfo label="Fellowship Certificates" index={1} isLoading />
              <FileCardInfo label="Fellowship Certificates" index={2} isLoading />
            </>
          )}
          {provider?.fellowship_certificates?.map((fellowship_certificate, i) => (
            <FileCardInfo
              key={fellowship_certificate.id}
              label="Fellowship Certificates"
              file={fellowship_certificate}
              index={i}
            />
          ))}
          {provider?.fellowship_certificates?.length === 0 && (
            <>No Fellowship Certificates registered</>
          )}
        </Card.SubCard>
      </Col>
      <Col xs={12} md={6} lg={4} className="mb-4">
        <Card.SubCard className="p-4 mb-4 h-100">
          <h5>ECFMGs</h5>
          {isProviderLoading && (
            <>
              <FileCardInfo label="ECFMGs" index={0} isLoading />
              <FileCardInfo label="ECFMGs" index={1} isLoading />
              <FileCardInfo label="ECFMGs" index={2} isLoading />
            </>
          )}
          {provider?.ecfmgs?.map((ecfmg, i) => (
            <FileCardInfo key={ecfmg.id} label="ECFMGs" file={ecfmg} index={i} />
          ))}
          {provider?.ecfmgs?.length === 0 && <>No ECFMGs registered</>}
        </Card.SubCard>
      </Col>
      <Col xs={12} md={6} lg={4} className="mb-4">
        <Card.SubCard className="p-4 mb-4 h-100">
          <h5>CMEs</h5>
          {isProviderLoading && (
            <>
              <FileCardInfo label="CMEs" index={0} isLoading />
              <FileCardInfo label="CMEs" index={1} isLoading />
              <FileCardInfo label="CMEs" index={2} isLoading />
            </>
          )}
          {provider?.cmes?.map((cme, i) => (
            <FileCardInfo key={cme.id} label="CMEs" file={cme} index={i} />
          ))}
          {provider?.cmes?.length === 0 && <>No CMEs registered</>}
        </Card.SubCard>
      </Col>
    </Row>
  );
};
